import { useCallback, useEffect, useState } from 'react';
import {
  ApiDonateCampaign,
  ApiDonor,
  ApiCampaignUpdate,
  createDonateCampaign,
  fetchDonateCampaigns,
  fetchDonateCampaign,
  updateDonateCampaign,
  donateToCampaign,
  requestCampaignWithdraw,
  addCampaignUpdate,
  CreateDonateCampaignPayload,
} from '../../api/payment/donate-campaign.api';

/**
 * "Tạo mục tiêu donate" — yêu cầu số 1 (REQ mục 4).
 * Đã nối backend thật (cab-payment-service). Mọi thao tác gọi API + refetch.
 */

export type GoalType = 'amount_time' | 'amount';
export type WithdrawStatus = 'none' | 'requested' | 'disbursed';
export type DonateCampaign = ApiDonateCampaign;
export type Donor = ApiDonor;
export type CampaignUpdate = ApiCampaignUpdate;


export const isGoalReached = (c: DonateCampaign): boolean =>
  c.targetAmount > 0 && c.raised >= c.targetAmount;

export const isEnded = (c: DonateCampaign): boolean =>
  c.goalType === 'amount_time' && !!c.endAt && new Date() >= new Date(c.endAt);

/** Trạng thái hiển thị của chiến dịch (suy ra từ withdrawStatus của BE + mục tiêu/thời gian). */
export type CampaignState = 'active' | 'stopped' | 'disbursing' | 'disbursed';
export const campaignState = (c: DonateCampaign): CampaignState => {
  if (c.withdrawStatus === 'disbursed') return 'disbursed';
  if (c.withdrawStatus === 'requested') return 'disbursing';
  if (isGoalReached(c) || isEnded(c)) return 'stopped'; // ngừng hoạt động
  return 'active';
};

export const useDonateCampaign = (ownerId?: string) => {
  const [campaigns, setCampaigns] = useState<DonateCampaign[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!ownerId) {
      setCampaigns([]);
      return;
    }
    setLoading(true);
    try {
      const list = await fetchDonateCampaigns(ownerId);
      setCampaigns(list || []);
    } catch {
      /* lỗi đã được interceptor xử lý; giữ danh sách cũ */
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const create = useCallback(
    async (payload: CreateDonateCampaignPayload) => {
      const c = await createDonateCampaign(payload);
      await refresh();
      return c;
    },
    [refresh]
  );

  const updateCampaign = useCallback(
    async (id: string, patch: { title?: string; content?: string; imageUrl?: string }) => {
      const c = await updateDonateCampaign(id, patch);
      await refresh();
      return c;
    },
    [refresh]
  );

  const addUpdate = useCallback(
    async (id: string, u: { content: string; imageUrl?: string; authorName?: string }) => {
      const c = await addCampaignUpdate(id, u);
      await refresh();
      return c;
    },
    [refresh]
  );

  const donate = useCallback(
    async (id: string, payload: { amount: number; message?: string; donorName?: string }) => {
      const c = await donateToCampaign(id, payload);
      await refresh();
      return c;
    },
    [refresh]
  );

  const requestWithdraw = useCallback(
    async (id: string) => {
      const c = await requestCampaignWithdraw(id);
      await refresh();
      return c;
    },
    [refresh]
  );

  const getById = useCallback((id: string) => fetchDonateCampaign(id), []);

  return {
    campaigns,
    loading,
    refresh,
    create,
    updateCampaign,
    addUpdate,
    donate,
    requestWithdraw,
    getById,
  };
};
